import { useSyncExternalStore } from "react";

/* ──────────────────────────────────────────────────────────────────────────
   Whether Shift is held down (Jay, 2026-10-08: holding Shift turns every
   to-do's ⋯ into its actions as buttons, to work through the list quickly).
   One listener for the whole page, however many rows read it. Letting go,
   or the window losing focus while it is held, turns it off again.
   ────────────────────────────────────────────────────────────────────────── */

let held = false;
const listeners = new Set<() => void>();

function set(next: boolean) {
  if (held === next) return;
  held = next;
  for (const listener of listeners) listener();
}

function onKey(event: KeyboardEvent) {
  // Shift while typing is for capitals, not for this.
  const target = event.target as HTMLElement | null;
  const typing = Boolean(target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable));
  set(event.shiftKey && !typing);
}

function onBlur() {
  set(false);
}

function subscribe(listener: () => void) {
  if (listeners.size === 0) {
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKey);
    window.addEventListener("blur", onBlur);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
      window.removeEventListener("blur", onBlur);
      held = false;
    }
  };
}

export function useShiftHeld(): boolean {
  return useSyncExternalStore(subscribe, () => held, () => false);
}
